import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot, UrlCreationOptions, UrlTree } from '@angular/router';
import { getVariantCodeFromUrl } from '@app/helpers';
import {
  CmsActivatedRouteSnapshot,
  CmsService,
  isNotUndefined,
  Page,
  PageContext,
  Product,
  ProductScope,
  ProductService,
  RoutingService,
  SemanticPathService,
} from 'haba-core';
import {
  CmsComponentsService,
  CmsGuardsService,
  CmsI18nService,
  CmsPageGuardService,
  CmsRoutesService,
} from 'haba-storefrontlib';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CmsProductPageGuardService extends CmsPageGuardService {
  rRoute: CmsActivatedRouteSnapshot;
  constructor(
    protected semanticPathService: SemanticPathService,
    protected cmsService: CmsService,
    protected cmsRoutes: CmsRoutesService,
    protected cmsI18n: CmsI18nService,
    protected cmsGuards: CmsGuardsService,
    protected cmsComponentsService: CmsComponentsService,
    protected routing: RoutingService,
    private router: Router,
    private productService: ProductService
  ) {
    super(
      semanticPathService,
      cmsService,
      cmsRoutes,
      cmsI18n,
      cmsGuards,
      cmsComponentsService,
      routing
    );
  }

  /** This is the implementation copied from Spartacus & we add our custom logic to last switchMap. This way we keep all spartacus functionalities presented in documentation. */
  canActivateProductPage(
    pageContext: PageContext,
    pageData: Page,
    route: CmsActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    this.rRoute = route;
    return this.cmsService.getPageComponentTypes(pageContext).pipe(
      take(1),
      switchMap((componentTypes) => this.cmsComponentsService.determineMappings(componentTypes)),
      switchMap((componentTypes) =>
        this.cmsGuards
          .cmsPageCanActivate(componentTypes, route, state)
          .pipe(withLatestFrom(of(componentTypes)))
      ),
      tap(([canActivate, componentTypes]) => {
        if (canActivate === true) {
          this.cmsI18n.loadForComponents(componentTypes);
        }
      }),
      map(([canActivate, componentTypes]) => {
        const pageLabel = pageData.label || pageContext.id; // for content pages the page label returned from backend can be different than ID initially assumed from route
        if (canActivate === true && !route?.data?.cxCmsRouteContext) {
          return this.cmsRoutes.handleCmsRoutesInGuard(
            pageContext,
            componentTypes,
            state.url,
            pageLabel
          );
        }
        return canActivate;
      }),
      switchMap((canActivate) => {
        if (!canActivate) {
          return of(canActivate);
        }
        return this.getActivateOrNewUrl(pageContext.id, route, state);
      })
    );
  }

  /** Match State URL with product URL, if we have a base product, we redirect to variant product, because base product cannot be purchased. */
  getActivateOrNewUrl(
    productCode: string,
    route: CmsActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    if (route.fragment) {
      productCode = getVariantCodeFromUrl('#' + route.fragment) || productCode;
    }
    if (!productCode) {
      return of(true);
    }
    return this.productService.get(productCode, ProductScope.DETAILS).pipe(
      filter(isNotUndefined),
      map((product: Product) => {
        if (product.productType === 'PARENT_VARIANT') {
          const productId = this.findPurchasableProductId(product);
          return this.createProductPath(productId);
        }
        if (!product.isApproved) {
          const substituteProduct: string =
            product.mixins?.['haba-product-sap-data']?.substituteProduct;
          const assortmentSites: string[] = (
            (product.mixins?.['haba-product-assortment-data']?.assortments ?? []) as {
              site?: string;
            }[]
          )
            .map((assortment) => assortment?.site)
            .filter((site): site is string => !!site);
          if (substituteProduct && assortmentSites.includes(product.siteCode)) {
            return this.router.createUrlTree([`/p/${substituteProduct}`]);
          }
          return this.router.createUrlTree(['/not-found']);
        }
        return true;
      })
    );
  }

  /**
   * Finds a purchasable product code looking at variant options, if any
   * @param product
   */
  protected findPurchasableProductId(product: Product): string {
    const variantOptions = product.mixins['haba-product-variant-options'];
    if (
      variantOptions &&
      Array.isArray(variantOptions.options) &&
      variantOptions.options.length > 0
    ) {
      const options = variantOptions.options;
      //we take first option as fallback if only one option is available or no other option matches
      //if first option is not approved, we get a 404, but we can not return undefined here
      let variant = options[0];
      if (
        Array.isArray(variantOptions.variantTypes) &&
        variantOptions.variantTypes.includes('LANGUAGE')
      ) {
        //if product has language variants, look for the one which has german language
        //todo use active language of site, at the moment only german is relevant
        variant =
          options.find(
            (option) =>
              Array.isArray(option.features) &&
              option.features.length > 0 &&
              option.features[0].valueCode === 'DE'
          ) || variant;
      } else if (options.length > 1) {
        //if it has other variants than language then look for the first one which is approved for current site
        variant =
          options
            .slice()
            .sort((option1, option2) => option1.code > option2.code)
            .find((option) => option.approvedSites?.split('|')?.includes(product.siteCode)) ||
          variant;
      }
      return variant.id;
    } else {
      //fall back to old logic if mixin is not available (old logic mixin comes from PXM and should always be there)
      return this.findPurchasableProductIdFallback(product);
    }
  }

  /**
   * Fallback with the old logic if new mixin is not yet available
   * @param product
   */
  protected findPurchasableProductIdFallback(product: Product): string | undefined {
    let variant = undefined;
    const variantOptions = product.mixins['haba-variants'];
    if (variantOptions && variantOptions.variants.length > 0) {
      //take first variant if available
      variant = variantOptions.variants[0];
      //check if the variant building attribute is of type langauge
      if (
        variantOptions.attributes &&
        variantOptions.attributes.find((attribute) => attribute.type === 'LANGUAGE')
      ) {
        //find variant with german langauge to show
        variant =
          variantOptions.variants.find((langVariant) => {
            return langVariant.features.find((feature) => feature.code === 'DE');
          }) || variant;
      }
    }
    return variant?.id;
  }

  /**  create product path from product url, if we have no url fragment backend doesn't add a trailing slash anymore. */
  private createProductPath(productId: string): UrlTree {
    const productCode = productId.split('--')[1];
    const urlTree = this.rRoute.url.join('/');
    const navigationExtras: UrlCreationOptions = {
      fragment: `variationId=${productCode}`, //variationId=1307037001
      queryParams: { ...this.rRoute.queryParams },
    };
    return this.router.createUrlTree([urlTree], navigationExtras);
  }
}
